// WebGL context-loss detection + guided recovery.
//
// Background: each open study tab creates its own Cornerstone RenderingEngine,
// which renders to a single offscreen WebGL canvas. Browsers cap live WebGL
// contexts per process (~16). With many studies open the browser drops the
// least-recently-used contexts → the image disappears (annotations, which are
// SVG, remain) and the patched renderWindow.render() throws the "Viewer is
// unable to perform the action" alert on every subsequent frame.
//
// Cornerstone3D v2 has no built-in context recovery (v4's context pool does),
// and we are intentionally NOT upgrading on this branch. A reliable in-place
// rebuild isn't possible because vtk.js actors/shaders stay bound to the lost
// context. So "recovery" here is: detect the loss once, stop the alert spam,
// and offer the user a one-click reload of just this tab (which re-acquires a
// context) instead of leaving them on a dead viewer. Reload is user-confirmed,
// never automatic, so unsaved report work is not lost without consent.

// Module-level guard so we only ever prompt once per page lifetime, no matter
// how many engines/viewports report the loss.
let recoveryPromptShown = false;

// Exposed so the patched RenderingEngine.render() (which can't import modules)
// can flag a context-loss episode and suppress its repeated alert. See
// patches/@cornerstonejs+core+2.17.6.patch.
if (typeof window !== "undefined") {
  window.__augmentoWebGLContextLost = window.__augmentoWebGLContextLost || false;
}

// Registered on window (below) so the patched RenderingEngine.render() — which
// runs as raw JS in node_modules and cannot import this module — can route its
// render-error path through the same de-duped, user-confirmed recovery prompt
// instead of alert()-ing on every frame.

const RECOVERY_MESSAGE =
  "The image viewer ran out of resources — this usually happens when " +
  "many studies are open at once. Reload this tab to restore the images? ";

/**
 * Show the one-time recovery prompt. Called both proactively (webglcontextlost
 * event) and reactively (from the render-error path via window hook). Guarded
 * so it fires at most once.
 */
export const promptWebGLRecovery = () => {
  if (recoveryPromptShown) return;
  recoveryPromptShown = true;
  if (typeof window !== "undefined") {
    window.__augmentoWebGLContextLost = true;
  }
  // Defer so we never run a blocking confirm() inside a render/rAF frame.
  setTimeout(() => {
    try {
      const shouldReload = window.confirm(RECOVERY_MESSAGE);
      if (shouldReload) {
        window.location.reload();
      }
    } catch (_) {
      // confirm() blocked (e.g. headless) — leave the tab as-is; the user can
      // still refresh manually.
    }
  }, 0);
};

// Expose the prompt to the patched render() error path (raw JS in node_modules).
if (typeof window !== "undefined") {
  window.__augmentoPromptWebGLRecovery = promptWebGLRecovery;
}

// Best-effort: reach the engine's offscreen WebGL context and return whether
// it has been lost. Returns false on any failure (never throws). vtk.js exposes
// the GL context via get3DContext() (older builds: getContext()).
const getEngineGLContext = (renderingEngine) => {
  try {
    const orw =
      renderingEngine &&
      renderingEngine.offscreenMultiRenderWindow &&
      renderingEngine.offscreenMultiRenderWindow.getOpenGLRenderWindow &&
      renderingEngine.offscreenMultiRenderWindow.getOpenGLRenderWindow();
    if (!orw) return null;
    if (typeof orw.get3DContext === "function") return orw.get3DContext();
    if (typeof orw.getContext === "function") return orw.getContext();
  } catch (_) {
    /* ignore */
  }
  return null;
};

/**
 * Detect a silently-lost WebGL context. The browser drops the least-recently-
 * used contexts when too many studies are open; vtk.js then renders BLACK
 * without throwing (and without always firing webglcontextlost), so this is a
 * needed backstop to the event listener. If the context is lost, fire the
 * one-time guided-reload prompt. Returns true if a loss was detected.
 *
 * Note: this catches a *formally lost* context (the common eviction case). The
 * cross-context "object does not belong to this context" state may not set the
 * lost flag, so this is a strong backstop, not a guarantee.
 */
export const checkEngineContextLost = (renderingEngine) => {
  const gl = getEngineGLContext(renderingEngine);
  if (gl && typeof gl.isContextLost === "function" && gl.isContextLost()) {
    console.warn(
      "[WebGL] offscreen context is lost (detected post-render) — the tab " +
        "likely lost its context to the browser's per-process limit."
    );
    promptWebGLRecovery();
    return true;
  }
  return false;
};

/**
 * Best-effort: find the engine's offscreen WebGL canvas and attach
 * webglcontextlost / webglcontextrestored listeners. Idempotent per engine
 * (guarded by a flag on the engine instance). Safe to call on every
 * getRenderingEngineLocal — wrapped in try/catch because it reaches into
 * vtk.js internals that aren't part of the public API.
 */
export const attachWebGLContextLossHandlers = (renderingEngine) => {
  try {
    if (!renderingEngine || renderingEngine.__augmentoCtxHandlersAttached) return;

    const orw =
      renderingEngine.offscreenMultiRenderWindow &&
      renderingEngine.offscreenMultiRenderWindow.getOpenGLRenderWindow &&
      renderingEngine.offscreenMultiRenderWindow.getOpenGLRenderWindow();
    if (!orw) return;

    // vtk.js OpenGLRenderWindow exposes the backing canvas via getCanvas();
    // fall back to the context's canvas if the accessor name differs.
    let canvas = null;
    if (typeof orw.getCanvas === "function") {
      canvas = orw.getCanvas();
    }
    if (!canvas && typeof orw.get3DContext === "function") {
      const ctx = orw.get3DContext();
      canvas = ctx && ctx.canvas;
    }
    if (!canvas || typeof canvas.addEventListener !== "function") return;

    canvas.addEventListener(
      "webglcontextlost",
      (event) => {
        // preventDefault lets the browser attempt a restore and keeps the
        // event from being treated as a hard, unrecoverable loss.
        event.preventDefault();
        console.warn(
          "[WebGL] context lost on the study viewer offscreen canvas — " +
            "likely the browser's per-process WebGL context limit was hit " +
            "(too many studies open)."
        );
        if (typeof window !== "undefined") {
          window.__augmentoWebGLContextLost = true;
        }
        promptWebGLRecovery();
      },
      false
    );

    canvas.addEventListener(
      "webglcontextrestored",
      () => {
        console.warn(
          "[WebGL] context restored. A reload is still required to rebuild " +
            "the viewer's GPU resources on Cornerstone3D v2."
        );
      },
      false
    );

    renderingEngine.__augmentoCtxHandlersAttached = true;
  } catch (err) {
    // Never let recovery wiring break engine creation.
    console.debug("[WebGL] could not attach context-loss handlers:", err);
  }
};
